import React, { useState, useEffect } from "react";
import {
  X,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  Edit,
  Trash2,
  Save,
  RefreshCw,
  AlertTriangle,
  Package,
  User,
  Phone,
  MapPin,
  DollarSign,
  Hash,
} from "lucide-react";

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (orders: any[]) => Promise<void>;
  accounts: Array<{ id: number; username: string }>;
  sources: Array<{ id: number; name: string }>;
  products: Array<{ id: number; name: string; price: number; currency: string }>;
  currentUser?: {
    role: string;
    countries?: string[];
  };
}

interface ParsedOrder {
  id: string; // Unique identifier for the preview table
  orderId?: string; // Original Order ID from Excel (if exists)
  fullName: string;
  source: string;
  totalPrice: string;
  fullAddress: string;
  phone: string;
  products: string;
  comment: string;
  // Parsed fields
  country: string;
  city: string;
  street: string;
  addr: string;
  area: string;
  zipcode: string;
  productId?: number;
  productName: string;
  quantity: number;
  price: number;
  currency: string;
  accountId?: number;
  sourceId?: number;
  // Status indicators
  isDuplicate: boolean;
  duplicateReason?: string;
  hasErrors: boolean;
  errors: string[];
  isEditing: boolean;
}

export const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  onSave,
  accounts,
  sources,
  products,
  currentUser,
}) => {
  const [pastedData, setPastedData] = useState("");
  const [parsedOrders, setParsedOrders] = useState<ParsedOrder[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [step, setStep] = useState<"paste" | "preview">("paste");

  if (!isOpen) return null;

  const parseExcelData = (data: string) => {
    setIsProcessing(true);
    try {
      // Split by lines and filter empty lines
      const lines = data.split("\n").filter((line) => line.trim());
      
      // Intelligently detect and skip header row(s)
      // Header detection: check if line contains keywords like "Order ID", "Name", "Price", etc.
      const headerKeywords = ["order id", "name", "full name", "price", "address", "phone", "product", "source", "total"];
      const dataLines = lines.filter(line => {
        const lowercaseLine = line.toLowerCase();
        // If line contains 2+ header keywords, it's likely a header
        const keywordMatches = headerKeywords.filter(keyword => lowercaseLine.includes(keyword)).length;
        const isHeader = keywordMatches >= 2;
        
        // Also check if "Total Price" column is not a valid number
        const columns = line.split("\t");
        if (columns.length >= 4) {
          const priceColumn = columns[3]?.trim();
          // If price column exists and is not a number (but contains letters), it's a header
          if (priceColumn && /[a-zA-Z]{3,}/.test(priceColumn)) {
            return false; // Skip this line (it's a header)
          }
        }
        
        return !isHeader;
      });
      
      const parsed: ParsedOrder[] = [];
      const seenPhones = new Map<string, number>(); // Track phone numbers
      
      dataLines.forEach((line, index) => {
        // Split by tab (Excel paste uses tabs)
        const columns = line.split("\t").map((col) => col.trim());
        
        if (columns.length < 7) return; // Skip invalid rows
        
        const [orderId, fullName, source, totalPrice, fullAddress, phone, productStr, , comment] = columns;
        
        if (!fullName || !phone) return; // Skip if essential fields are missing
        
        // Parse address (city, street, zipcode)
        const addressParsed = parseAddress(fullAddress);
        
        // Parse products and quantity
        const productParsed = parseProducts(productStr, products);
        
        // Parse price
        const priceParsed = parsePrice(totalPrice);
        
        // Find matching source
        const matchedSource = findMatchingSource(source, sources);
        
        // Find matching account (default to first if none specified)
        const matchedAccount = accounts.length > 0 ? accounts[0].id : undefined;
        
        // Check for duplicates
        const phoneKey = phone.replace(/\D/g, ""); // Remove non-digits
        const isDuplicate = seenPhones.has(phoneKey);
        if (!isDuplicate) {
          seenPhones.set(phoneKey, parsed.length);
        }
        
        // Validate and collect errors
        const errors: string[] = [];
        if (!addressParsed.city || addressParsed.city === "EDIT_REQUIRED") {
          errors.push("City not found - please edit");
        }
        if (!addressParsed.zipcode || addressParsed.zipcode === "EDIT_REQUIRED") {
          errors.push("Zipcode (5 digits) not found - please edit");
        }
        if (!addressParsed.area || addressParsed.area === "EDIT_REQUIRED") {
          errors.push("Area/Region not found - please edit");
        }
        if (!productParsed.productId) {
          errors.push(`Product "${productParsed.productName}" not matched in system`);
        }
        if (!matchedSource) {
          errors.push(`Source "${source}" not matched in system`);
        }
        
        // Phone validation: must be 10 digits (without country code) or 12 digits (with 30 prefix)
        const cleanPhone = phone.replace(/\D/g, "");
        if (!cleanPhone.match(/^(\d{10}|30\d{10})$/)) {
          errors.push(`Invalid phone: "${phone}" (must be 10 digits)`);
        }
        
        // Note: ADDR # (address number) is optional - not added to errors
        
        parsed.push({
          id: `temp-${Date.now()}-${index}`,
          orderId: orderId || undefined,
          fullName,
          source,
          totalPrice,
          fullAddress,
          phone,
          products: productStr,
          comment: comment || "",
          country: addressParsed.country,
          city: addressParsed.city,
          street: addressParsed.street,
          addr: addressParsed.addr,
          area: addressParsed.area,
          zipcode: addressParsed.zipcode,
          productId: productParsed.productId,
          productName: productParsed.productName,
          quantity: productParsed.quantity,
          price: priceParsed.price,
          currency: priceParsed.currency,
          accountId: matchedAccount,
          sourceId: matchedSource,
          isDuplicate,
          duplicateReason: isDuplicate ? `Duplicate phone: ${phone}` : undefined,
          hasErrors: errors.length > 0,
          errors,
          isEditing: false,
        });
      });
      
      setParsedOrders(parsed);
      setStep("preview");
    } catch (error) {
      console.error("Error parsing Excel data:", error);
      alert("Error parsing data. Please check the format.");
    } finally {
      setIsProcessing(false);
    }
  };

  const parseAddress = (fullAddress: string) => {
    // Parse Greek addresses in various formats
    // Try comma-separated first: "Street Number, City Zipcode"
    // Then try space-separated: "ΠΑΡΘΕΝΗ 8 ΓΕΡΑΚΑΣ 15344"
    
    let street = "";
    let addr = "";
    let city = "";
    let area = "";
    let zipcode = "";
    
    // Try comma-separated format first
    if (fullAddress.includes(",")) {
      const commaParts = fullAddress.split(",").map(p => p.trim());
      
      if (commaParts.length >= 2) {
        // First part: street and address number
        const streetPart = commaParts[0].trim();
        const streetWords = streetPart.split(/\s+/);
        
        // Find address number in street part
        for (let i = 0; i < streetWords.length; i++) {
          if (/^\d{1,3}[Α-Ωα-ω]?$/.test(streetWords[i])) {
            addr = streetWords[i];
            street = streetWords.slice(0, i).concat(streetWords.slice(i + 1)).join(" ");
            break;
          }
        }
        if (!street) street = streetPart;
        
        // Second part: city and zipcode
        const cityPart = commaParts[1].trim();
        const cityWords = cityPart.split(/\s+/);
        
        // Find zipcode
        for (const word of cityWords) {
          if (/^\d{5}$/.test(word)) {
            zipcode = word;
            city = cityWords.filter(w => w !== zipcode).join(" ");
            break;
          }
        }
        if (!city) city = cityPart;
        area = city;
      }
    }
    
    // If comma parsing didn't work, try space-separated
    if (!city || !zipcode) {
      const parts = fullAddress.trim().split(/\s+/);
      
      // Find zipcode (5 digits)
      let zipcodeIndex = -1;
      for (let i = 0; i < parts.length; i++) {
        if (/^\d{5}$/.test(parts[i])) {
          zipcode = parts[i];
          zipcodeIndex = i;
          break;
        }
      }
      
      // City is usually right before zipcode
      if (zipcodeIndex > 0) {
        city = parts[zipcodeIndex - 1];
      }
      
      // Extract address number (single/double digit, possibly with Greek letter)
      let addrIndex = -1;
      for (let i = 0; i < parts.length && i < zipcodeIndex - 1; i++) {
        if (/^\d{1,3}[Α-Ωα-ω]?$/.test(parts[i])) {
          addr = parts[i];
          addrIndex = i;
          break;
        }
      }
      
      // Street is everything before address number (or city if no address number found)
      if (addrIndex > 0) {
        street = parts.slice(0, addrIndex).join(" ");
      } else if (zipcodeIndex > 1) {
        street = parts.slice(0, zipcodeIndex - 1).join(" ");
      } else {
        street = parts.slice(0, -2).join(" ");
      }
      
      // Area - try to extract region or use city
      area = city;
      const commonAreas = ["ATTICA", "ΑΤΤΙΚΗΣ", "ΑΤΤΙΚΉ", "ΘΕΣΣΑΛΟΝΙΚΗΣ", "ΠΑΤΡΑΣ", "ΗΡΑΚΛΕΙΟΥ"];
      for (const part of parts) {
        const upperPart = part.toUpperCase();
        if (commonAreas.some(a => upperPart.includes(a) || a.includes(upperPart))) {
          area = part;
          break;
        }
      }
    }
    
    // If parsing still failed, put everything in street and user will edit
    if (!city || !zipcode) {
      return {
        country: currentUser?.countries?.[0]?.toLowerCase() || "gr",
        city: city || "EDIT_REQUIRED",
        street: fullAddress,
        addr: addr,
        area: area || "EDIT_REQUIRED",
        zipcode: zipcode || "EDIT_REQUIRED",
      };
    }
    
    return {
      country: currentUser?.countries?.[0]?.toLowerCase() || "gr",
      city: city,
      street: street || fullAddress,
      addr: addr,
      area: area || city,
      zipcode: zipcode,
    };
  };

  const parseProducts = (productStr: string, availableProducts: typeof products) => {
    // Extract quantity and product name
    // Examples: "2x Diet Shake", "BRAIN ACTIVE (2+1)", "3x PROSTATOL", "2 ENDURO", "3 Χ COLLAGEN PEPTIDES"
    
    let quantity = 1;
    let productName = productStr.trim();
    
    // Pattern 1: "2x", "3X", "2×" (number + x/X/×)
    const qtyMatchX = productStr.match(/^(\d+)\s*[xX×Χχ]\s*/i);
    if (qtyMatchX) {
      quantity = parseInt(qtyMatchX[1]);
      productName = productStr.replace(qtyMatchX[0], "").trim();
    }
    // Pattern 2: "2 ENDURO", "3 Χ COLLAGEN" (number + space + optional X + product)
    else {
      const qtyMatchSpace = productStr.match(/^(\d+)\s+[XΧχ]?\s*/i);
      if (qtyMatchSpace) {
        quantity = parseInt(qtyMatchSpace[1]);
        productName = productStr.replace(qtyMatchSpace[0], "").trim();
      }
    }
    
    // Pattern 3: Bundle notation "(2+1)" meaning 3 items total
    const bundleMatch = productStr.match(/\((\d+)\+(\d+)\)/);
    if (bundleMatch) {
      quantity = parseInt(bundleMatch[1]) + parseInt(bundleMatch[2]);
      productName = productStr.replace(bundleMatch[0], "").trim();
    }
    
    // Find matching product (fuzzy match)
    const matched = availableProducts.find((p) => {
      const searchStr = productName.toLowerCase();
      const productNameLower = p.name.toLowerCase();
      return (
        productNameLower.includes(searchStr) ||
        searchStr.includes(productNameLower) ||
        productNameLower.includes("prostatol") && searchStr.includes("prostatol") ||
        productNameLower.includes("collagen") && searchStr.includes("collagen") ||
        productNameLower.includes("brain") && searchStr.includes("brain") ||
        productNameLower.includes("diet") && searchStr.includes("diet") ||
        productNameLower.includes("whey") && searchStr.includes("whey") ||
        productNameLower.includes("enduro") && searchStr.includes("enduro") ||
        productNameLower.includes("snail") && searchStr.includes("snail")
      );
    });
    
    return {
      productId: matched?.id,
      productName: matched?.name || productName,
      quantity,
    };
  };

  const parsePrice = (priceStr: string) => {
    // Parse price and extract currency if present
    // Examples: "54,9", "69", "60.00 EUR", "60,00 €", "54.9 USD"
    
    let currency = "eur"; // Default to EUR for Greece
    let priceOnly = priceStr;
    
    // Check if currency is present in the string
    const currencyMatch = priceStr.match(/[A-Z]{3}$/i); // Match 3-letter currency code at end
    if (currencyMatch) {
      currency = currencyMatch[0].toLowerCase();
      priceOnly = priceStr.replace(currencyMatch[0], "").trim();
    } else if (priceStr.includes("€")) {
      currency = "eur";
      priceOnly = priceStr.replace("€", "").trim();
    } else if (priceStr.includes("$")) {
      currency = "usd";
      priceOnly = priceStr.replace("$", "").trim();
    }
    
    // Clean and parse the price
    const cleanPrice = priceOnly.replace(/[,\s]/g, ".").replace(/\.+/g, ".");
    const price = parseFloat(cleanPrice) || 0;
    
    return {
      price,
      currency,
    };
  };

  const findMatchingSource = (sourceStr: string, availableSources: typeof sources) => {
    // Match source names
    const sourceLower = sourceStr.toLowerCase().trim();
    const matched = availableSources.find((s) => 
      s.name.toLowerCase().includes(sourceLower) ||
      sourceLower.includes(s.name.toLowerCase())
    );
    return matched?.id;
  };

  const handlePaste = () => {
    if (!pastedData.trim()) {
      alert("Please paste Excel data first");
      return;
    }
    parseExcelData(pastedData);
  };

  const handleRemoveOrder = (id: string) => {
    setParsedOrders((prev) => prev.filter((o) => o.id !== id));
  };

  const handleToggleEdit = (id: string) => {
    setParsedOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, isEditing: !o.isEditing } : o))
    );
  };

  const handleEditOrder = (id: string, field: string, value: any) => {
    setParsedOrders((prev) =>
      prev.map((o) =>
        o.id === id
          ? {
              ...o,
              [field]: value,
              hasErrors: false, // Clear errors when editing
              errors: [],
            }
          : o
      )
    );
  };

  const handleSaveAll = async () => {
    // Check for orders that still need editing
    const ordersNeedingEdit = parsedOrders.filter(
      (o) => o.city === "EDIT_REQUIRED" || o.zipcode === "EDIT_REQUIRED" || o.area === "EDIT_REQUIRED"
    );
    
    if (ordersNeedingEdit.length > 0) {
      alert(
        `${ordersNeedingEdit.length} order(s) need editing (marked with EDIT_REQUIRED).\n` +
        `Please click the Edit button (pencil icon) to fix them before importing.`
      );
      return;
    }
    
    // Filter out orders with errors or duplicates
    const validOrders = parsedOrders.filter((o) => !o.hasErrors && !o.isDuplicate);
    
    if (validOrders.length === 0) {
      alert("No valid orders to import");
      return;
    }
    
    if (window.confirm(`Import ${validOrders.length} orders?`)) {
      setIsSaving(true);
      try {
        // Convert to backend format
        const ordersToSave = validOrders.map((o) => {
          // Clean phone number (remove spaces, dashes, parentheses)
          let cleanPhone = o.phone.replace(/[\s\-\(\)]/g, "");
          
          // Add Greece country code (30) if not present and phone is 10 digits
          if (cleanPhone.length === 10 && !cleanPhone.startsWith("30")) {
            cleanPhone = "30" + cleanPhone;
          }
          
          return {
            // Original order ID if exists
            originalOrderId: o.orderId,
            // Customer info
            name: o.fullName,
            phone: cleanPhone,
            email: "",
            gender: 0,
            // Address
            country: o.country.toLowerCase(),
            city: o.city,
            street: o.street,
            addr: o.addr,
            area: o.area,
            index: o.zipcode,
            // Product
            offer: o.productId!,
            offername: o.productName,
            count: o.quantity,
            price: o.price,
            currency: o.currency.toLowerCase(),
            // Account and source
            user: o.accountId!,
            wm: o.sourceId!,
            // Status
            status: 6, // Always packaging for manual orders
            stage: 0,
            // Source indicator
            source: "manual",
            // Comment
            comment: o.comment,
          };
        });
        
        await onSave(ordersToSave);
        onClose();
      } catch (error) {
        console.error("Error saving orders:", error);
        alert("Failed to import orders. Please try again.");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const renderPasteStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FileSpreadsheet className="w-6 h-6 text-[#264a3e] mr-2" />
          <div>
            <h4 className="text-sm font-medium text-gray-900">
              Paste Excel Data
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Copy from Excel and paste below (Ctrl+V)
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Expected Format:</p>
            <p className="text-xs">
              Order ID | Full Name | Source | Total Price | Full Address | Phone | Products | Comment
            </p>
          </div>
        </div>
      </div>

      <textarea
        value={pastedData}
        onChange={(e) => setPastedData(e.target.value)}
        placeholder="Paste Excel data here (with headers)..."
        className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e]"
      />

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {pastedData.split("\n").filter((l) => l.trim()).length - 1} rows detected
        </div>
        <button
          onClick={handlePaste}
          disabled={!pastedData.trim() || isProcessing}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50"
        >
          {isProcessing ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Parse Data
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900">
            Preview & Edit Orders
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Review, edit, or remove orders before importing
          </p>
        </div>
        <button
          onClick={() => {
            setParsedOrders([]);
            setPastedData("");
            setStep("paste");
          }}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          <RefreshCw className="w-4 h-4 inline mr-1" />
          Start Over
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <div>
              <p className="text-xs text-green-600 font-medium">Valid</p>
              <p className="text-lg font-bold text-green-900">
                {parsedOrders.filter((o) => !o.hasErrors && !o.isDuplicate).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-3">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <div>
              <p className="text-xs text-red-600 font-medium">Errors</p>
              <p className="text-lg font-bold text-red-900">
                {parsedOrders.filter((o) => o.hasErrors).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-3">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
            <div>
              <p className="text-xs text-yellow-600 font-medium">Duplicates</p>
              <p className="text-lg font-bold text-yellow-900">
                {parsedOrders.filter((o) => o.isDuplicate).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Phone
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  City
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Area/Region
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Street
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Addr #
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Zipcode
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Product
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Qty
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Price
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parsedOrders.map((order) => (
                <tr
                  key={order.id}
                  className={`${
                    order.hasErrors
                      ? "bg-red-50"
                      : order.isDuplicate
                      ? "bg-yellow-50"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td className="px-2 py-2 whitespace-nowrap">
                    {order.hasErrors ? (
                      <div className="relative group cursor-help" title={order.errors.join("\n")}>
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <div className="hidden group-hover:block absolute z-10 w-64 p-2 mt-1 text-xs text-white bg-red-600 rounded shadow-lg left-0">
                          <div className="font-semibold mb-1">Errors:</div>
                          {order.errors.map((err, idx) => (
                            <div key={idx}>• {err}</div>
                          ))}
                        </div>
                      </div>
                    ) : order.isDuplicate ? (
                      <div className="relative group cursor-help" title={order.duplicateReason}>
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                        <div className="hidden group-hover:block absolute z-10 w-48 p-2 mt-1 text-xs text-white bg-yellow-600 rounded shadow-lg left-0">
                          {order.duplicateReason}
                        </div>
                      </div>
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[180px]">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.fullName}
                        onChange={(e) => handleEditOrder(order.id, "fullName", e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900">{order.fullName}</div>
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[120px]">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.phone}
                        onChange={(e) => handleEditOrder(order.id, "phone", e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{order.phone}</div>
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[120px]">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.city}
                        onChange={(e) => handleEditOrder(order.id, "city", e.target.value)}
                        className={`w-full px-2 py-1 text-sm border rounded ${
                          order.city === "EDIT_REQUIRED" ? "border-red-500 bg-red-50" : "border-gray-300"
                        }`}
                        placeholder="City"
                      />
                    ) : (
                      <div className={`text-sm ${order.city === "EDIT_REQUIRED" ? "text-red-600 font-bold" : "text-gray-900"}`}>
                        {order.city}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[120px]">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.area}
                        onChange={(e) => handleEditOrder(order.id, "area", e.target.value)}
                        className={`w-full px-2 py-1 text-sm border rounded ${
                          order.area === "EDIT_REQUIRED" ? "border-red-500 bg-red-50" : "border-gray-300"
                        }`}
                        placeholder="Area/Region"
                      />
                    ) : (
                      <div className={`text-sm ${order.area === "EDIT_REQUIRED" ? "text-red-600 font-bold" : "text-gray-900"}`}>
                        {order.area}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[200px]">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.street}
                        onChange={(e) => handleEditOrder(order.id, "street", e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="Street"
                      />
                    ) : (
                      <div className="text-sm text-gray-900" title={order.street}>
                        {order.street}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.addr}
                        onChange={(e) => handleEditOrder(order.id, "addr", e.target.value)}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="#"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{order.addr || "-"}</div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.zipcode}
                        onChange={(e) => handleEditOrder(order.id, "zipcode", e.target.value)}
                        className={`w-20 px-2 py-1 text-sm border rounded ${
                          order.zipcode === "EDIT_REQUIRED" ? "border-red-500 bg-red-50" : "border-gray-300"
                        }`}
                        placeholder="12345"
                      />
                    ) : (
                      <div className={`text-sm ${order.zipcode === "EDIT_REQUIRED" ? "text-red-600 font-bold" : "text-gray-900"}`}>
                        {order.zipcode}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[200px]">
                    {order.isEditing ? (
                      <input
                        type="text"
                        value={order.productName}
                        onChange={(e) => handleEditOrder(order.id, "productName", e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    ) : (
                      <div className="text-sm text-gray-900" title={order.productName}>
                        {order.productName}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {order.isEditing ? (
                      <input
                        type="number"
                        value={order.quantity}
                        onChange={(e) => handleEditOrder(order.id, "quantity", parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                        min="1"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{order.quantity}</div>
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[100px]">
                    {order.isEditing ? (
                      <div className="flex items-center space-x-1">
                        <input
                          type="number"
                          step="0.01"
                          value={order.price}
                          onChange={(e) => handleEditOrder(order.id, "price", parseFloat(e.target.value) || 0)}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                        <span className="text-xs text-gray-500">{order.currency.toUpperCase()}</span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-900">
                        {order.price.toFixed(2)} {order.currency.toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleToggleEdit(order.id)}
                        className={`${
                          order.isEditing ? "text-green-600 hover:text-green-800" : "text-blue-600 hover:text-blue-800"
                        }`}
                        title={order.isEditing ? "Save" : "Edit"}
                      >
                        {order.isEditing ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleRemoveOrder(order.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Upload className="w-5 h-5 mr-2 text-[#264a3e]" />
            Bulk Import Orders
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === "paste" ? renderPasteStep() : renderPreviewStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {step === "preview" && (
              <>
                {parsedOrders.filter((o) => !o.hasErrors && !o.isDuplicate).length} orders ready to import
              </>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
            >
              Cancel
            </button>
            {step === "preview" && (
              <button
                onClick={handleSaveAll}
                disabled={isSaving || parsedOrders.filter((o) => !o.hasErrors && !o.isDuplicate).length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Save className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Import Orders
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;

